import { NearestFilter, ShaderMaterial, Texture, Vector3 } from 'three';

import fragmentShader from '../../../../../static/glsl/cesium/cesium-points.frag';
import vertexShader from '../../../../../static/glsl/cesium/cesium-tiles-points.vert';
import { TextureManager } from '../../../utilities/texture-manager/texture-manager';

export class Cesium3dTilesPointsMaterial {
  static get instance() {
    return Cesium3dTilesPointsMaterial._instance;
  }

  get displayClasses(): boolean {
    return this.shared.displayClasses.value;
  }
  set displayClasses(state: boolean) {
    this.shared.displayClasses.value = state;
  }

  /**
   * Cesium geometricErrorScale equivalent.
   * 1.0 = a tile's geometric error fills its exact projected screen-space area.
   * Larger value → smaller points. Default 1.0.
   * Tune live: window.__pc.pointSizeScale
   */
  get pointSizeScale(): number {
    return this.shared.pointSizeScale.value;
  }
  set pointSizeScale(v: number) {
    this.shared.pointSizeScale.value = v;
  }

  /** Maximum rendered point size in pixels. Tune live: window.__pc.maximumAttenuation */
  get maximumAttenuation(): number {
    return this.shared.maximumAttenuation.value;
  }
  set maximumAttenuation(v: number) {
    this.shared.maximumAttenuation.value = v;
  }

  get attenuation(): boolean {
    return this.shared.attenuation.value;
  }
  set attenuation(v: boolean) {
    this.shared.attenuation.value = v;
  }

  get pointScale(): number {
    return this.shared.pointScale.value;
  }
  set pointScale(v: number) {
    this.shared.pointScale.value = v;
  }

  set lightDirection(v: Vector3) {
    this.shared.lightDirection.value = v;
  }

  set applyShading(v: boolean) {
    this.shared.applyShading.value = v;
  }

  set opacity(v: number) {
    this.shared.opacity.value = v;
  }

  private static _instance = new Cesium3dTilesPointsMaterial();

  /**
   * Uniform objects shared by reference across every per-tile ShaderMaterial.
   * Writing `.value` on any of these propagates to all tile instances
   * automatically — no iteration needed.
   */
  readonly shared: {
    texture1: { value: Texture | undefined };
    displayClasses: { value: boolean };
    applyShading: { value: boolean };
    attenuation: { value: boolean };
    /** Cesium geometricErrorScale: 1.0 = error fills its projected screen area */
    pointSizeScale: { value: number };
    /** Canvas height in pixels — updated each frame by setCameraAndResolution() */
    screenHeight: { value: number };
    /** Maximum gl_PointSize in pixels — prevents close-range blobs */
    maximumAttenuation: { value: number };
    pointScale: { value: number };
    opacity: { value: number };
    lightDirection: { value: Vector3 };
  };

  private constructor() {
    this.shared = {
      texture1: { value: undefined },
      displayClasses: { value: false },
      applyShading: { value: true },
      attenuation: { value: true },
      pointSizeScale: { value: 1.0 },
      screenHeight: { value: 1080 },
      maximumAttenuation: { value: 10.0 },
      pointScale: { value: 1.0 },
      opacity: { value: 1.0 },
      lightDirection: { value: new Vector3(0.5, 0.5, 1.0).normalize() },
    };
  }

  async initialize() {
    // TextureManager caches by URL so this reuses the same Texture object as
    // CesiumPointsMaterial — both materials share the same GPU texture and any
    // classification visibility toggle (pixel alpha) affects both automatically.
    const texture = await TextureManager.instance.loadSync(
      '/static/assets/textures/point-cloud-classification-colors.png',
    );
    this.setTexture(texture);
  }

  setTexture(texture: Texture) {
    texture.magFilter = NearestFilter;
    texture.minFilter = NearestFilter;
    this.shared.texture1.value = texture;
    texture.needsUpdate = true;
  }

  /**
   * Creates a ShaderMaterial for one tile.
   *
   * All uniforms except `pointSize` are the shared objects from this singleton —
   * changes to displayClasses, lightDirection, pointSizeScale, pointScale etc.
   * propagate to every tile material automatically without iteration.
   *
   * @param geometricError - the tile's own geometricError (world units).
   *   The renderer updates this each frame based on whether children are loaded.
   * @param options.withoutNormals - for points without normals, which are then lit head on.
   * @param options.adaptiveOctree - sizes each point by the deepest octree level drawn where it
   *   is, from the visibleNodes, nodeIndex, cubeCenter and cubeHalfSize uniforms, see the COPC
   *   point cloud.
   */
  createTileInstance(
    geometricError: number,
    options: { withoutNormals?: boolean; adaptiveOctree?: boolean } = {},
  ): ShaderMaterial {
    const u = this.shared;
    const defines: Record<string, string> = {};
    if (options.withoutNormals) {
      defines.NO_NORMALS = '';
    }
    if (options.adaptiveOctree) {
      defines.ADAPTIVE_OCTREE = '';
    }
    return new ShaderMaterial({
      defines,
      vertexShader,
      fragmentShader,
      uniforms: {
        // Shared by reference — global writes propagate automatically.
        texture1: u.texture1,
        displayClasses: u.displayClasses,
        applyShading: u.applyShading,
        attenuation: u.attenuation,
        pointSizeScale: u.pointSizeScale,
        screenHeight: u.screenHeight,
        maximumAttenuation: u.maximumAttenuation,
        pointScale: u.pointScale,
        opacity: u.opacity,
        lightDirection: u.lightDirection,
        // Per-tile: updated each frame by the renderer.
        pointSize: { value: geometricError },
        ...(options.adaptiveOctree && {
          visibleNodes: { value: null },
          nodeIndex: { value: -1 },
          cubeCenter: { value: new Vector3() },
          cubeHalfSize: { value: 1 },
        }),
      },
      clipping: true,
      vertexColors: true,
      transparent: true,
    });
  }
}
